import { DateTime } from "luxon";
import { formatDuration } from "../../lib/duration";

/** `YYYY-MM-DD` is a calendar day in some location's zone; format it as such, never through the browser's zone. */
export function formatDay(day: string): string {
  return DateTime.fromISO(day, { zone: "utc" }).toFormat("ccc, LLL d");
}

export function formatInstant(iso: string, zone?: string): string {
  const dt = DateTime.fromISO(iso);
  return (zone ? dt.setZone(zone) : dt).toFormat("LLL d, h:mm a");
}

/** Durations read in hours ("1.5 hours"); see `formatDuration`. */
export function formatMinutes(min: number): string { return formatDuration(min); }

export function formatPct(pct: number): string { return `${Math.round(pct)}%`; }

/**
 * Relative to `now` in the viewer's zone (default: the browser's). Day names use that zone's
 * day boundary, so an event late last evening reads "Yesterday" even when UTC has not rolled over.
 */
export function formatRelative(iso: string | null, now: Date, zone?: string): string {
  if (iso === null) return "—";
  const z = zone ?? DateTime.local().zoneName;
  const at = DateTime.fromISO(iso).setZone(z);
  const ref = DateTime.fromJSDate(now).setZone(z);
  const ms = ref.toMillis() - at.toMillis();
  if (ms >= 0) {
    if (ms < 60_000) return "just now";
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 6 * 3_600_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  }
  // Rounded: across a DST change two local midnights are 23 or 25 hours apart, not 24.
  const days = Math.round(at.startOf("day").diff(ref.startOf("day"), "days").days);
  const time = at.toFormat("h:mm a");
  if (days === 0) return `Today ${time}`;
  if (days === -1) return `Yesterday ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  return at.toFormat("LLL d, h:mm a");
}

export function formatAbsolute(iso: string, zone?: string): string {
  return DateTime.fromISO(iso).setZone(zone ?? DateTime.local().zoneName).toFormat("LLL d, yyyy, h:mm a ZZZZ");
}

/** Active time in compact form: "45m", "2h", "1h 15m". */
export function formatActiveTime(min: number): string {
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

/** Whole calendar days ago in the viewer's zone: "today", "yesterday", "4 days ago". */
export function formatDaysAgo(iso: string, now: Date): string {
  const z = DateTime.local().zoneName;
  const days = Math.round(DateTime.fromJSDate(now).setZone(z).startOf("day").diff(DateTime.fromISO(iso).setZone(z).startOf("day"), "days").days);
  if (days <= 0) return "today";
  return days === 1 ? "yesterday" : `${days} days ago`;
}
